import React from 'react';

import { EmptyTutorial } from '@dashboard/components/empty-states/EmptyTutorial';
import { createTableComponent } from '@dashboard/components/pages/QueryListPage/TableList/WpDataGrid';
import SimpleTextCell, { SimpleDateCell, SimpleNumberCell } from '@dashboard/components/table/cells/SimpleTextCell';
import { FContactFragment } from '@dashboard/generated/wp/platform';
import usePlatformNavigator from '@dashboard/hooks/navigate/usePlatformNavigator';
import { Link, Paper, Typography } from '@mui/material';

import PageHeader from './PageHeader';
import useCurrentContacts from './useCurrentContacts';

const ContactsListTable = createTableComponent<FContactFragment>({
  emptyResults: (
    <EmptyTutorial
      description={
        <>
          <Typography variant="h4" gutterBottom>
            You don't have any contacts yet
          </Typography>
          <Typography color="text.secondary">
            {' '}
            Start sending emails to see your contacts at a glance and drill down into each to see full timeline of
            events.{' '}
            <Link target="_blank" href="https://www.usewaypoint.com/docs/contacts">
              Learn more
            </Link>
            .
          </Typography>
        </>
      }
      videoSource="https://assets.usewaypoint.com/teaser-contacts-list-voiceover.mp4"
      videoTitle="Video teaser of contacts on Waypoint"
    />
  ),

  columns: [
    {
      headerName: 'Email',
      renderCell: ({ row }) => <SimpleTextCell>{row.emailAddress}</SimpleTextCell>,
    },
    {
      headerName: 'Name',
      renderCell: ({ row }) => <SimpleTextCell>{row.name ?? ''}</SimpleTextCell>,
    },
    {
      headerName: 'ID',
      renderCell: ({ row }) => <SimpleTextCell>{row.id}</SimpleTextCell>,
    },
    {
      headerName: 'Last message sent',
      renderCell: ({ row }) => <SimpleDateCell value={row.lastEmailMessageAt} />,
    },
    {
      headerName: 'Messages',
      renderCell: ({ row }) => <SimpleNumberCell value={row.emailMessagesCount} />,
    },
  ],
});

export default function ContactsList() {
  const { data } = useCurrentContacts();
  const navigator = usePlatformNavigator();

  const nodes = data?.Contacts.nodes;
  return (
    <Paper>
      <PageHeader />
      <ContactsListTable
        nodes={nodes}
        onRowClick={(row) => {
          const modelIds = nodes?.map((n) => n.id) ?? [];
          navigator.showContact(row.id, { modelIds });
        }}
      />
    </Paper>
  );
}
